import { useEffect, useState } from "react";
import { Sparkles, CreditCard, CheckCircle2, ChevronRight, Ban } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Billing() {
    const { user, refresh } = useAuth();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checking, setChecking] = useState(false);

    useEffect(() => {
        api.get("/billing/plan")
            .then((r) => setPlan(r.data))
            .catch(() => toast.error("Errore caricamento piano"))
            .finally(() => setLoading(false));
    }, []);

    async function onSubscribe() {
        setChecking(true);
        try {
            const r = await api.post("/billing/checkout", { origin_url: window.location.origin });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setChecking(false);
        }
    }

    async function onCancel() {
        if (!window.confirm("Vuoi davvero annullare l'abbonamento?")) return;
        try {
            await api.post("/billing/cancel", {});
            toast.success("Abbonamento annullato");
            await refresh();
            const r = await api.get("/billing/plan");
            setPlan(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    }

    if (loading) return <div className="text-stone-500 text-sm">Caricamento...</div>;
    if (!plan) return null;

    const status = plan.user.subscription_status;
    const trialDaysLeft =
        plan.user.trial_ends_at
            ? Math.max(0, Math.ceil((new Date(plan.user.trial_ends_at) - Date.now()) / 86400000))
            : 0;

    return (
        <div className="space-y-6 max-w-3xl" data-testid="billing-page">
            {(status === "canceled" || status === "expired") && user?.role !== "admin" && (
                <div className="card-soft p-5 border-2 border-orange-200 bg-orange-50/50" data-testid="billing-inactive-banner">
                    <div className="flex items-start gap-3">
                        <Ban size={22} className="text-orange-600 mt-0.5 shrink-0" />
                        <div>
                            <h3 className="font-display text-lg text-stone-800 mb-1">Il tuo abbonamento è {status === "canceled" ? "annullato" : "scaduto"}</h3>
                            <p className="text-sm text-stone-600">
                                Per riprendere ad usare HACCP Manager devi rinnovare l'abbonamento qui sotto. I tuoi dati sono al sicuro e tornerai a vederli appena il pagamento sarà confermato.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <div className="card-soft p-6">
                <div className="flex items-center gap-2 mb-1">
                    <CreditCard size={18} className="text-forest" />
                    <h2 className="font-display text-xl text-forest-dark">Abbonamento</h2>
                </div>
                <p className="text-sm text-stone-500 mb-5">
                    Gestisci il tuo piano HACCP Manager.
                </p>

                <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between p-3 bg-bone-2 rounded-xl">
                        <span className="text-sm text-stone-600">Piano attuale</span>
                        <span className="font-semibold text-forest-dark" data-testid="billing-current-plan">
                            {status === "active"
                                ? "Mensile 9,99€"
                                : status === "trialing"
                                ? `Prova gratuita (${trialDaysLeft} ${trialDaysLeft === 1 ? "giorno" : "giorni"})`
                                : status === "canceled"
                                ? "Annullato"
                                : "Scaduto"}
                        </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-bone-2 rounded-xl">
                        <span className="text-sm text-stone-600">Stato</span>
                        <span
                            className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                                plan.user.active ? "bg-forest text-white" : "bg-orange-100 text-orange-700"
                            }`}
                            data-testid="billing-status-badge"
                        >
                            {plan.user.active ? "Attivo" : "Inattivo"}
                        </span>
                    </div>
                </div>

                {user?.role !== "admin" && status !== "active" && (
                    <div className="rounded-xl border-2 border-forest bg-forest/5 p-5">
                        <div className="flex items-start gap-2 mb-3">
                            <Sparkles size={20} className="text-forest mt-0.5" />
                            <div>
                                <h3 className="font-display text-lg text-forest-dark">Passa al piano Mensile</h3>
                                <p className="text-sm text-stone-600 mt-1">
                                    Continua a usare tutte le funzionalità: HACCP, fatture OCR, scadenze, PDF, notifiche push.
                                </p>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3 mb-4">
                            {[
                                "Tutto incluso",
                                "Frigoriferi illimitati",
                                "OCR fatture illimitato",
                                "Disdici quando vuoi",
                            ].map((f) => (
                                <div key={f} className="flex items-center gap-2 text-sm text-stone-700">
                                    <CheckCircle2 size={14} className="text-forest" />
                                    {f}
                                </div>
                            ))}
                        </div>
                        <div className="flex items-baseline gap-1 mb-4">
                            <span className="font-display text-3xl text-forest-dark">{plan.plan.price_eur.toFixed(2)}</span>
                            <span className="text-stone-500">€/mese</span>
                        </div>
                        <button
                            onClick={onSubscribe}
                            disabled={checking}
                            className="btn-primary w-full inline-flex items-center justify-center gap-2"
                            data-testid="billing-subscribe-btn"
                        >
                            {checking ? "Apertura Stripe..." : "Sottoscrivi ora"} <ChevronRight size={16} />
                        </button>
                        <p className="text-xs text-stone-500 text-center mt-3">
                            Pagamento sicuro tramite Stripe · Nessun vincolo · Annulla in qualsiasi momento
                        </p>
                    </div>
                )}

                {status === "active" && user?.role !== "admin" && (
                    <button
                        onClick={onCancel}
                        className="btn-ghost w-full text-red-600 hover:bg-red-50"
                        data-testid="billing-cancel-btn"
                    >
                        Annulla abbonamento
                    </button>
                )}

                {user?.role === "admin" && (
                    <div className="rounded-xl bg-accent-warm/10 text-accent-warm p-4 text-sm">
                        Sei admin: hai accesso completo senza abbonamento.
                    </div>
                )}
            </div>
        </div>
    );
}
